/**
 * PUBLIC WEBSITE CONTENT — edit this file to change anything on the public site.
 * Nothing in here touches private campaign data.
 *
 * Copy in this file is taken from AnyTribe's own material: the live website and the
 * "AnyTribe — Creator-Led Growth Playbook" deck. Published figures come from that deck and were
 * approved for public use by the agency owner (15 Sep 2026).
 *
 * Cover images marked `coverIsPlaceholder: true` are neutral stock photography standing in for real
 * campaign assets. They render with a visible "Placeholder image" badge. To replace one: drop in the
 * real image URL and set `coverIsPlaceholder: false`.
 */

export const LOGO_URL =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_campaign-hub-demo-4/artifacts/az67160u_1.png";

/** Canonical public domain for the AnyTribe website (used for SEO canonical + share cards). */
export const SITE_URL = "https://www.anytribemarketing.com";

export const POSITIONING = "Creator Marketing Partner · Malaysia";

export const HERO = {
  headline: "People trust people.",
  headlineParts: { lead: "People ", accent: "trust", tail: " people." },
  statement: "We help you find the right ones.",
  supporting:
    "We connect brands with the right KOLs and creators to build trust and turn influence into measurable brand growth.",
  platforms: "Instagram · XiaoHongShu · TikTok · Threads",
  tagline: "Turning authentic influence into measurable brand growth.",
};

export const FUNNEL = {
  eyebrow: "What we do",
  title: "One Partner, The Whole Creator Funnel",
  body:
    "Most brands stitch this together across three vendors and lose the handover every time. We run it as one connected system, briefed together and measured together.",
  logic:
    "The logic: creators create demand → ads scale what already works → search captures the intent → community keeps it warm.",
};

export const SERVICES = [
  {
    slug: "kol-marketing",
    stage: "Core · Create demand",
    name: "KOL Marketing",
    description:
      "Creator sourcing, briefing, delivery and reporting — the engine everything else runs on.",
    icon: "Users",
  },
  {
    slug: "content-creation",
    stage: "Supply · Own the assets",
    name: "Content Creation",
    description:
      "Brand-owned assets produced in creator language — calendar, captions, and platform-native execution.",
    icon: "Clapperboard",
  },
  {
    slug: "threads-seeding",
    stage: "Amplify · Sustain",
    name: "Threads & Seeder Posting",
    description:
      "Conversation seeding and reply-layer management, so the campaign keeps working after the post goes live.",
    icon: "MessagesSquare",
  },
  {
    slug: "performance-ads",
    stage: "Scale · Buy reach",
    name: "Performance Ads",
    description:
      "Paid amplification of creator content that has already proven it performs organically. Meta and TikTok.",
    icon: "TrendingUp",
  },
  {
    slug: "seo-sem",
    stage: "Capture · Convert intent",
    name: "SEO & SEM",
    description:
      "Search visibility for the demand creator content creates, so the searches it triggers land on you.",
    icon: "Search",
  },
  {
    slug: "community-events",
    stage: "Activate · Retain",
    name: "Community & Events",
    description:
      "On-ground activation and always-on community building, delivered through our events arm @joinsaltandsoul.",
    icon: "CalendarHeart",
  },
];

export const ENGAGEMENT_MODELS = {
  eyebrow: "Commercials",
  title: "How to Work With Us",
  body:
    "Four ways in, scaled to objective and commitment. All four plug into direct-brand and agency-led workflows.",
  models: [
    {
      id: "model-01",
      label: "Model 01",
      title: "Campaign-based creator activation",
      bestFor: "Best for a launch, promo or seasonal push.",
      description:
        "Scoped per campaign: sourcing, negotiation, briefing, delivery and a close-out report.",
    },
    {
      id: "model-02",
      label: "Model 02",
      title: "Monthly always-on creator management",
      bestFor: "Best for sustained retail and category visibility.",
      description: "A rolling creator roster with committed monthly output and monthly reporting.",
    },
    {
      id: "model-03",
      label: "Model 03",
      title: "Product launch & event support",
      bestFor: "Best for a dated moment that has to land.",
      description:
        "Creator activation, on-ground coverage and post-event amplification, with Salt & Soul on the ground.",
    },
    {
      id: "model-04",
      label: "Model 04 · Full funnel",
      title: "Integrated creator + growth retainer",
      bestFor: "Best for brands that want the whole funnel under one team.",
      description:
        "KOL, threads and seeding, performance ads, SEO/SEM and content — briefed together, reported in one view.",
    },
  ],
  addOn:
    "Community-building support is available as an add-on to any model, or as a standalone engagement through @joinsaltandsoul.",
};

export const OPERATING_MODEL = {
  eyebrow: "How we work",
  title: "One loop, five steps",
  body:
    "Every campaign runs through the same loop. Each pass teaches us something we carry into the next one, so performance compounds instead of resetting.",
  steps: [
    {
      key: "influence",
      name: "Influence",
      short: "Mapping",
      body:
        "We map the creators whose audiences already trust them on your category, then shortlist by audience fit rather than follower count.",
    },
    {
      key: "creative",
      name: "Creative",
      short: "Development",
      body:
        "We shape the angle with each creator instead of handing over a script, so the content sounds like them and still lands your message.",
    },
    {
      key: "execution",
      name: "Execution",
      short: "Delivery",
      body:
        "Briefing, negotiation, posting windows, links and deliverables are managed end to end, so nothing depends on chasing people over chat.",
    },
    {
      key: "learning",
      name: "Learning",
      short: "Measurement",
      body:
        "Reported metrics are collected per post and per creator, with the capture date attached, so what worked is a fact rather than a feeling.",
    },
    {
      key: "scale",
      name: "Scale",
      short: "Compounding",
      body:
        "What performed gets more budget, more creators and paid amplification. What did not gets cut before it costs you another cycle.",
    },
  ],
  loopNote: "The loop restarts with what the last round proved - not from scratch.",
};

export const PRINCIPLES = {
  eyebrow: "Our approach",
  title: "What Makes AnyTribe Different",
  body: "We don't just run influencer campaigns — we build creator systems that perform.",
  items: [
    {
      label: "Principle 01",
      title: "Start with the audience, not the influencer",
      body:
        "We identify who needs to be influenced, then match them with the creators their audience already trusts — rather than starting from a follower list.",
    },
    {
      label: "Principle 02",
      title: "Design content to perform, not just to post",
      body:
        "We shape hooks, angles and formats built to drive awareness, engagement and conversion — the brief specifies the job, not only the mention.",
    },
    {
      label: "Principle 03",
      title: "Build collaborations that feel authentic",
      body:
        "The best-performing content happens when brand objectives meet creator voice naturally. We protect the voice that earned the audience.",
    },
  ],
  extra:
    "And one more: every campaign is briefed with what happens after the post — seeding, amplification and search are planned in, not added on.",
};

/**
 * BRANDS WE'VE WORKED WITH
 * `logo` expects a file dropped into /public/logos (e.g. "/logos/guardian.svg").
 * Until the real logo files are supplied, each tile renders the brand name as a wordmark and
 * `logosPending` shows an honest notice. Set `logo` per brand to swap a tile over - nothing else
 * needs to change. Brand names are taken verbatim from the owner's brief.
 */
export const BRANDS = {
  heading: "Brands We've Worked With",
  supporting:
    "Experience across retail, beauty, wellness, technology, food, travel, and events\u2014through direct-brand and agency-led collaborations.",
  disclaimer:
    "Work delivered through direct-brand and agency-led collaborations. Not all brands listed are direct clients or currently active accounts.",
  logosPending: false,
  items: [
    { name: "Guardian", logo: "/logos/guardian.jpg" },
    { name: "Pos Malaysia", logo: "/logos/pos-malaysia.jpg" },
    { name: "Aiken", logo: "/logos/aiken.jpg" },
    { name: "CARSOME", logo: "/logos/carsome.jpg" },
    { name: "VIVAIA", logo: "/logos/vivaia.jpg" },
    { name: "Samsung", logo: "/logos/samsung.jpg" },
    { name: "MARIGOLD", logo: "/logos/marigold.jpg" },
    { name: "KFC", logo: "/logos/kfc.jpg" },
    { name: "McDonald's", logo: "/logos/mcdonalds.jpg" },
    { name: "Traveloka", logo: "/logos/traveloka.jpg" },
    { name: "LEGOLAND", logo: "/logos/legoland.jpg" },
    { name: "Kawan", logo: "/logos/kawan.jpg" },
    { name: "Safi", logo: "/logos/safi.jpg" },
    { name: "AIA", logo: "/logos/aia.jpg" },
    { name: "Dynamo", logo: "/logos/dynamo.jpg" },
    { name: "Blackmores", logo: "/logos/blackmores.jpg" },
    { name: "MILO", logo: "/logos/milo.jpg" },
    { name: "Abbott", logo: "/logos/abbott.jpg" },
    { name: "Flavettes", logo: "/logos/flavettes.jpg" },
    { name: "Berocca", logo: "/logos/berocca.jpg" },
    { name: "OLDTOWN White Coffee", logo: "/logos/oldtown-white-coffee.jpg" },
    { name: "realme", logo: "/logos/realme.jpg" },
  ],
};

export const AGENCY_PARTNERS = {
  heading: "Also delivered through agency partners",
  supporting:
    "A trusted execution partner for creator campaigns, working across both direct-brand and agency-led workflows.",
  items: [
    { name: "Passionation.co", logo: "/logos/passionation-co.png" },
    { name: "OMD", logo: "/logos/omd.png" },
    { name: "PHD", logo: "/logos/phd.png" },
  ],
};

export const AWARD = {
  heading: "Celebrating Award-Winning Campaign Work",
  campaign: "Pos Malaysia Run 2025",
  award: "Gold",
  category: "Best Use of Influencers / KOLs",
  event: "Marketing Events Awards 2026",
  campaignYear: "2025",
  awardYear: "2026",
  copy:
    "Pos Malaysia Run 2025 won Gold for Best Use of Influencers / KOLs at the Marketing Events Awards 2026. We're proud to have supported the campaign's creator marketing execution.",
  note:
    "The award recognises the Pos Malaysia Run 2025 campaign. AnyTribe supported its creator marketing execution alongside the wider campaign team.",
};

export const CREATOR_POOL = {
  eyebrow: "Creator network",
  title: "A Ready Pool of Retail-Friendly Creators",
  body:
    "A flexible creator pool built for relatable, retail-driven content across beauty, wellness and everyday lifestyle.",
  categories: ["Beauty", "Skincare", "Wellness", "Personal Care", "Lifestyle", "Foodie", "Parenting"],
  audiences: "Chinese, Malay and mixed-audience creators across all categories.",
  enables: [
    "Coverage across key consumer categories, from everyday creators to established KOLs",
    "Suitable for promo, product review, event and always-on storytelling",
    "Diverse profiles for more relatable, localised brand communication",
  ],
  image:
    "https://images.unsplash.com/photo-1758272421249-7bc1066c6633?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400&h=1050&fit=crop",
  imageIsPlaceholder: true,
};

export const SCOPING = {
  eyebrow: "Next step",
  title: "Let's Scope Your Next Campaign",
  body:
    "Send us the objective, the window and a budget range. We'll come back with a creator mix, indicative reach and a cost per outcome — not a rate card.",
  steps: [
    {
      n: "Step 1",
      title: "Share the brief",
      body: "Objective, audience, timing and budget range. A paragraph is enough to start.",
    },
    {
      n: "Step 2",
      title: "We return a plan",
      body: "Creator mix by tier, projected reach, deliverables and cost per outcome.",
    },
    {
      n: "Step 3",
      title: "Align and go live",
      body: "You approve the shortlist and the angles. We handle everything after that.",
    },
  ],
};

export const ABOUT = {
  eyebrow: "About AnyTribe",
  title: "A creator marketing partner built around trust",
  paragraphs: [
    "AnyTribe is a creator marketing partner based in Malaysia. We connect brands with the right KOLs and creators to build trust, reach engaged audiences, and deliver measurable campaign performance.",
    "We don't just run influencer campaigns — we build creator systems that perform. That means the whole creator funnel under one team: KOL marketing, content creation, threads and seeding, performance ads, SEO and SEM, and on-ground community and events.",
    "Most brands stitch this together across three vendors and lose the handover every time. We run it as one connected system, briefed together and measured together — so each campaign briefs the next one better.",
  ],
  facts: [
    { label: "Based in", value: "Malaysia" },
    { label: "Creator pool", value: "Everyday creators to established KOLs" },
    { label: "Core platforms", value: "Instagram · XiaoHongShu · TikTok" },
    { label: "Events arm", value: "Salt & Soul" },
  ],
};

export const CONTACT = {
  headline: "Let's build something people actually trust",
  body:
    "Whether you're a brand looking for the right creators or a creator looking for the right brands, we're here to help.",
  email: "hello@anytribemarketing.com",
  phone: "+6010-2177169",
  phoneHref: "tel:+60102177169",
  instagram: "@anytribe.co",
  instagramUrl: "https://www.instagram.com/anytribe.co/",
  website: "www.anytribemarketing.com",
  websiteUrl: SITE_URL,
  location: "Malaysia",
  saltAndSoul: {
    name: "Salt & Soul",
    handle: "@joinsaltandsoul",
    url: "https://www.instagram.com/joinsaltandsoul/",
    description: "Our events and community arm — on-ground activation and always-on community building.",
  },
};

export const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Contact", to: "/contact" },
];
